import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Clock } from "lucide-react";

const COLORS = ["#6366f1", "#8b5cf6", "#ec4899", "#10b981", "#f59e0b", "#ef4444", "#06b6d4", "#f97316"];
const LANGS = ["de", "fr", "it"];
const LANG_LABELS = { de: "🇩🇪 DE", fr: "🇫🇷 FR", it: "🇮🇹 IT" };

const emptyTranslations = { de: { name: "", description: "" }, fr: { name: "", description: "" }, it: { name: "", description: "" } };
const emptyService = { name: "", description: "", duration: "30", price: "", buffer_time: "10", is_active: true, color: "#6366f1", translations: emptyTranslations };

export default function ServicesPage() {
  const { t } = useTranslation();
  const [services, setServices] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyService);
  const [activeLang, setActiveLang] = useState("de");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => toast.error(t("services_load_error")));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(emptyService); setActiveLang("de"); setDialogOpen(true); };
  const openEdit = (svc) => {
    setEditing(svc);
    const tr = svc.translations && typeof svc.translations === "object" ? svc.translations : {};
    const translations = {
      de: { name: tr.de?.name || "", description: tr.de?.description || "" },
      fr: { name: tr.fr?.name || "", description: tr.fr?.description || "" },
      it: { name: tr.it?.name || "", description: tr.it?.description || "" },
    };
    setForm({ name: svc.name, description: svc.description || "", duration: String(svc.duration), price: String(svc.price), buffer_time: String(svc.buffer_time), is_active: svc.is_active, color: svc.color || "#6366f1", translations });
    setActiveLang("de");
    setDialogOpen(true);
  };

  const setTranslation = (lang, field, value) => {
    setForm((f) => ({ ...f, translations: { ...f.translations, [lang]: { ...f.translations[lang], [field]: value } } }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error(t("services_name_required")); return; }
    setSaving(true);
    try {
      const payload = { ...form, duration: parseInt(form.duration), price: parseFloat(form.price || "0"), buffer_time: parseInt(form.buffer_time) };
      if (editing) {
        await api.put(`/services/${editing.id}`, payload);
        toast.success(t("services_updated"));
      } else {
        await api.post("/services", payload);
        toast.success(t("services_created"));
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("services_save_error"));
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await api.delete(`/services/${deleteConfirm.id}`);
      toast.success(t("services_deleted"));
      setDeleteConfirm(null);
      load();
    } catch { toast.error(t("services_delete_error")); }
  };

  return (
    <div data-testid="services-page" className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight font-heading text-white">{t("services_title")}</h1>
        <Button onClick={openCreate} className="bg-indigo-500 hover:bg-indigo-600 text-white" data-testid="add-service-button">
          <Plus size={16} className="mr-1" /> {t("services_add")}
        </Button>
      </div>

      {services.length === 0 ? (
        <div className="text-center py-16 text-slate-500">
          <p className="text-lg mb-2">{t("services_empty")}</p>
          <p className="text-sm">{t("services_empty_sub")}</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {services.map((svc) => (
            <div key={svc.id} className="bg-slate-900/50 border border-slate-800 rounded-xl p-5 hover:border-indigo-500/30 transition-colors group" data-testid={`service-card-${svc.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="w-3 h-3 rounded-full shrink-0 mt-1" style={{ backgroundColor: svc.color }} />
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-white" onClick={() => openEdit(svc)} data-testid={`edit-service-${svc.id}`}>
                    <Pencil size={14} />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-red-400" onClick={() => setDeleteConfirm(svc)} data-testid={`delete-service-${svc.id}`}>
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
              <h3 className="text-base font-semibold text-white mb-1">{svc.name}</h3>
              {svc.description && <p className="text-xs text-slate-400 mb-3 line-clamp-2">{svc.description}</p>}
              <div className="flex items-center gap-4 text-xs text-slate-400">
                <span className="flex items-center gap-1"><Clock size={12} />{svc.duration}min</span>
                <span className="font-mono">CHF {svc.price.toFixed(2)}</span>
              </div>
              {!svc.is_active && <Badge className="mt-2 bg-slate-700/50 text-slate-400 border-slate-600 text-[10px]">{t("inactive")}</Badge>}
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? t("services_edit") : t("services_new")}</DialogTitle>
            <DialogDescription className="text-slate-400">{editing ? t("services_edit_sub") : t("services_new_sub")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">{t("name")}</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("services_name_placeholder")} className="bg-slate-800 border-slate-700" data-testid="service-name-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">{t("description")}</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder={t("services_desc_placeholder")} className="bg-slate-800 border-slate-700" data-testid="service-description-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300 text-sm">{t("services_duration")}</Label>
                <Select value={form.duration} onValueChange={(v) => setForm({ ...form, duration: v })}>
                  <SelectTrigger className="bg-slate-800 border-slate-700" data-testid="service-duration-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {[15, 30, 45, 60, 90, 120].map((d) => (
                      <SelectItem key={d} value={String(d)}>{d} min</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300 text-sm">{t("services_price")}</Label>
                <Input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="0.00" className="bg-slate-800 border-slate-700" data-testid="service-price-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">{t("services_buffer")}</Label>
              <Select value={form.buffer_time} onValueChange={(v) => setForm({ ...form, buffer_time: v })}>
                <SelectTrigger className="bg-slate-800 border-slate-700" data-testid="service-buffer-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {[0, 5, 10, 15, 30].map((b) => (
                    <SelectItem key={b} value={String(b)}>{b} min</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">{t("services_color")}</Label>
              <div className="flex gap-2 flex-wrap">
                {COLORS.map((c) => (
                  <button key={c} onClick={() => setForm({ ...form, color: c })}
                    className={`w-7 h-7 rounded-lg transition-all ${form.color === c ? "ring-2 ring-white ring-offset-2 ring-offset-slate-900 scale-110" : "hover:scale-105"}`}
                    style={{ backgroundColor: c }} data-testid={`color-${c}`} />
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-slate-300 text-sm">{t("active")}</Label>
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="service-active-switch" />
            </div>

            {/* Translations */}
            <div className="border border-slate-700 rounded-lg p-3 space-y-3">
              <Label className="text-slate-400 text-xs uppercase tracking-wider">{t("services_translations")}</Label>
              <div className="flex gap-1">
                {LANGS.map((l) => (
                  <button key={l} onClick={() => setActiveLang(l)}
                    className={`px-3 py-1 rounded text-xs font-medium transition-colors ${activeLang === l ? "bg-indigo-500 text-white" : "bg-slate-800 text-slate-400 hover:text-white"}`}>
                    {LANG_LABELS[l]}
                  </button>
                ))}
              </div>
              <div className="space-y-2">
                <Input
                  value={form.translations[activeLang].name}
                  onChange={(e) => setTranslation(activeLang, "name", e.target.value)}
                  placeholder={t("services_trans_name_placeholder")}
                  className="bg-slate-800 border-slate-700 text-sm"
                />
                <Input
                  value={form.translations[activeLang].description}
                  onChange={(e) => setTranslation(activeLang, "description", e.target.value)}
                  placeholder={t("services_trans_desc_placeholder")}
                  className="bg-slate-800 border-slate-700 text-sm"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)} className="text-slate-400">{t("cancel")}</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-indigo-500 hover:bg-indigo-600" data-testid="save-service-button">
              {saving ? t("loading") : t("save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!deleteConfirm} onOpenChange={(o) => !o && setDeleteConfirm(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">{t("services_delete_confirm_title")}</DialogTitle>
            <DialogDescription className="text-slate-400">
              {t("services_delete_confirm_sub", { name: deleteConfirm?.name })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteConfirm(null)} className="text-slate-400">{t("cancel")}</Button>
            <Button onClick={handleDelete} className="bg-red-500 hover:bg-red-600 text-white">{t("delete")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
